import React, { useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LanguageContext } from '../../App';
import { stories } from './SuccessStories';

interface Story {
  id: string;
  name: string;
  story: string;
  image: string;
  category: string;
  date: string;
  fullStory: string;
}

const translations = {
  en: {
    backToStories: '← Back to Stories',
    notFound: 'Story not found',
    publishedOn: 'Published on',
    category: 'Category',
    share: 'Share Story',
    relatedStories: 'Related Stories'
  },
  hi: {
    backToStories: '← कहानियों पर वापस जाएं',
    notFound: 'कहानी नहीं मिली',
    publishedOn: 'प्रकाशित',
    category: 'श्रेणी',
    share: 'कहानी साझा करें',
    relatedStories: 'संबंधित कहानियां'
  }
};

const StoryDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { language } = useContext(LanguageContext);
  const t = translations[language];

  // Find the story in the current language
  const story = stories[language].find((s: Story) => s.id === id);

  if (!story) {
    return (
      <div className="container-custom py-20 text-center">
        <h1 className="text-2xl font-baloo text-gray-800">{t.notFound}</h1>
        <Link to="/" className="text-primary hover:text-primary/80 mt-4 inline-block">
          {t.backToStories}
        </Link>
      </div>
    );
  }

  return (
    <motion.article
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="py-12"
    >
      <div className="container-custom">
        {/* Navigation */}
        <Link
          to="/"
          className="text-primary hover:text-primary/80 mb-8 inline-block font-hindi"
        >
          {t.backToStories}
        </Link>

        {/* Hero Section */}
        <div className="relative h-[400px] rounded-xl overflow-hidden mb-8">
          <img
            src={story.image}
            alt={story.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
            <div className="inline-block bg-primary px-4 py-1 rounded-full text-sm mb-4">
              {story.category}
            </div>
            <h1 className="text-4xl md:text-5xl font-baloo font-bold mb-2">
              {story.name}
            </h1>
            <p className="text-white/80 font-hindi">
              {t.publishedOn} {story.date}
            </p>
          </div>
        </div>

        {/* Story Content */}
        <div className="max-w-3xl mx-auto">
          <div className="prose prose-lg font-hindi">
            {story.fullStory.split('\n').map((paragraph: string, index: number) => (
              <p key={index} className="whitespace-pre-wrap">
                {paragraph.trim()}
              </p>
            ))}
          </div>

          {/* Share Section */}
          <div className="mt-12 border-t border-gray-200 pt-8">
            <h3 className="text-xl font-baloo font-bold text-gray-800 mb-4">
              {t.share}
            </h3>
            <div className="flex gap-4">
              <button className="btn-outline-primary">
                <svg xmlns="/images/ROSHNI.jpg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
              </button>
              <button className="btn-outline-primary">
                <svg xmlns="/images/asha_image.png" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
                </svg>
              </button>
              <button className="btn-outline-primary">
                <svg xmlns="/images/transforming_image.jpg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm3 8h-1.35c-.538 0-.65.221-.65.778v1.222h2l-.209 2h-1.791v7h-3v-7h-2v-2h2v-2.308c0-1.769.931-2.692 3.029-2.692h1.971v3z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.article>
  );
};

export default StoryDetail; 